import {
  FilePreflightError,
  preflightRasterFile,
  type ImageMeta,
} from '@/shared/image/filePreflight'

export type ImageFormat = 'png' | 'jpeg'
export type OutputFormat = 'image/jpeg' | 'image/png'
export type ConversionDirection = 'pngToJpg' | 'jpgToPng'
export type ConverterLandingSource = 'png-v-jpg' | 'jpg-v-png' | 'canonical'

export interface ConverterLanding {
  direction: ConversionDirection
  source: ConverterLandingSource
}

export const MAX_CONVERTER_FILE_SIZE = 50 * 1024 * 1024
export const MAX_CONVERTER_PIXELS = 36_000_000
export const MAX_CONVERTER_SIDE = 16_384
export const MAX_CONVERTER_BATCH_FILES = 20
export const MAX_CONVERTER_BATCH_INPUT_BYTES = 200 * 1024 * 1024
export const MAX_CONVERTER_BATCH_PIXELS = 144_000_000
export const MAX_CONVERTER_OUTPUT_ZIP_WORK_BYTES = 512 * 1024 * 1024
export const JPEG_BACKGROUND = '#FFFFFF'
const RGBA_BYTES_PER_PIXEL = 4
const OUTPUT_AND_ZIP_COPIES = 2

export const DIRECTION_CONFIG: Record<ConversionDirection, {
  input: ImageFormat
  output: OutputFormat
  utilityKey: 'png-v-jpg' | 'jpg-v-png'
}> = {
  pngToJpg: {
    input: 'png',
    output: 'image/jpeg',
    utilityKey: 'png-v-jpg',
  },
  jpgToPng: {
    input: 'jpeg',
    output: 'image/png',
    utilityKey: 'jpg-v-png',
  },
}

export function directionForOutput(output: OutputFormat): ConversionDirection {
  return output === 'image/jpeg' ? 'pngToJpg' : 'jpgToPng'
}

export function resolveConverterLanding(search: string): ConverterLanding {
  const params = new URLSearchParams(search)
  const input = params.get('input')
  const output = params.get('output')
  const requestedSource = params.get('source')

  const direction: ConversionDirection =
    input === 'jpg' && output === 'png' ? 'jpgToPng' : 'pngToJpg'
  const expectedSource = DIRECTION_CONFIG[direction].utilityKey

  return {
    direction,
    source: requestedSource === expectedSource ? requestedSource : 'canonical',
  }
}

export function analyticsParams(
  direction: ConversionDirection,
  source: ConverterLandingSource,
): {
  direction: 'png-to-jpg' | 'jpg-to-png'
  landing_source: ConverterLandingSource
} {
  return {
    direction: direction === 'pngToJpg' ? 'png-to-jpg' : 'jpg-to-png',
    landing_source: source,
  }
}

export async function detectImageFormat(file: File): Promise<ImageFormat | null> {
  const bytes = new Uint8Array(await file.slice(0, 8).arrayBuffer())
  if (
    bytes.length >= 8
    && bytes[0] === 0x89
    && bytes[1] === 0x50
    && bytes[2] === 0x4e
    && bytes[3] === 0x47
    && bytes[4] === 0x0d
    && bytes[5] === 0x0a
    && bytes[6] === 0x1a
    && bytes[7] === 0x0a
  ) {
    return 'png'
  }
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'jpeg'
  }
  return null
}

export type ConverterFileValidation =
  | { ok: true; format: ImageFormat; meta: ImageMeta }
  | {
      ok: false
      reason: 'unsupported' | 'invalid_container' | 'too_many_pixels'
    }

export async function validateConverterFile(file: File): Promise<ConverterFileValidation> {
  const format = await detectImageFormat(file)
  if (!format) return { ok: false, reason: 'unsupported' }

  const normalizedFile = new File([file], format === 'png' ? 'input.png' : 'input.jpg', {
    type: format === 'png' ? 'image/png' : 'image/jpeg',
  })
  try {
    const meta = await preflightRasterFile(normalizedFile, {
      maxFileBytes: MAX_CONVERTER_FILE_SIZE,
      maxImagePixels: MAX_CONVERTER_PIXELS,
      maxLongestSide: MAX_CONVERTER_SIDE,
    })
    return { ok: true, format, meta }
  } catch (error) {
    if (error instanceof FilePreflightError && error.code === 'too_many_pixels') {
      return { ok: false, reason: 'too_many_pixels' }
    }
    return { ok: false, reason: 'invalid_container' }
  }
}

export function outputFilename(inputName: string, outputFormat: OutputFormat): string {
  const extension = outputFormat === 'image/jpeg' ? '.jpg' : '.png'
  const base = inputName.replace(/\.[^.]+$/, '')
  return `${base}${extension}`
}

export function canvasQuality(qualityPercent: number): number {
  return Math.min(100, Math.max(10, qualityPercent)) / 100
}

export interface ConverterBatchUsage {
  files: number
  inputBytes: number
  pixels: number
  outputZipWorkBytes: number
}

export type ConverterBatchLimitReason =
  | 'too_many_files'
  | 'aggregate_input_too_large'
  | 'aggregate_pixels_too_large'
  | 'output_zip_workload_too_large'

export type ConverterBatchAppendResult =
  | { ok: true; usage: ConverterBatchUsage }
  | { ok: false; reason: ConverterBatchLimitReason }

export const EMPTY_CONVERTER_BATCH_USAGE: ConverterBatchUsage = {
  files: 0,
  inputBytes: 0,
  pixels: 0,
  outputZipWorkBytes: 0,
}

export function appendConverterBatchUsage(
  current: ConverterBatchUsage,
  fileBytes: number,
  meta: Pick<ImageMeta, 'width' | 'height'>,
): ConverterBatchAppendResult {
  const pixels = meta.width * meta.height
  const next: ConverterBatchUsage = {
    files: current.files + 1,
    inputBytes: current.inputBytes + fileBytes,
    pixels: current.pixels + pixels,
    outputZipWorkBytes:
      current.outputZipWorkBytes
      + pixels * RGBA_BYTES_PER_PIXEL * OUTPUT_AND_ZIP_COPIES,
  }

  if (next.files > MAX_CONVERTER_BATCH_FILES) {
    return { ok: false, reason: 'too_many_files' }
  }
  if (next.inputBytes > MAX_CONVERTER_BATCH_INPUT_BYTES) {
    return { ok: false, reason: 'aggregate_input_too_large' }
  }
  if (next.pixels > MAX_CONVERTER_BATCH_PIXELS) {
    return { ok: false, reason: 'aggregate_pixels_too_large' }
  }
  if (next.outputZipWorkBytes > MAX_CONVERTER_OUTPUT_ZIP_WORK_BYTES) {
    return { ok: false, reason: 'output_zip_workload_too_large' }
  }
  return { ok: true, usage: next }
}

export function converterBatchUsage(
  entries: readonly { fileBytes: number; width: number; height: number }[],
): ConverterBatchUsage {
  return entries.reduce<ConverterBatchUsage>((usage, entry) => ({
    files: usage.files + 1,
    inputBytes: usage.inputBytes + entry.fileBytes,
    pixels: usage.pixels + entry.width * entry.height,
    outputZipWorkBytes:
      usage.outputZipWorkBytes
      + entry.width * entry.height * RGBA_BYTES_PER_PIXEL * OUTPUT_AND_ZIP_COPIES,
  }), EMPTY_CONVERTER_BATCH_USAGE)
}

export function isConverterZipWorkloadAllowed(outputSizes: readonly number[]): boolean {
  return outputSizes.length <= MAX_CONVERTER_BATCH_FILES
    && outputSizes.reduce((total, size) => total + size, 0) * OUTPUT_AND_ZIP_COPIES
      <= MAX_CONVERTER_OUTPUT_ZIP_WORK_BYTES
}

export function drawForOutput(
  context: Pick<CanvasRenderingContext2D, 'fillStyle' | 'fillRect' | 'drawImage'>,
  image: CanvasImageSource,
  width: number,
  height: number,
  outputFormat: OutputFormat,
): void {
  if (outputFormat === 'image/jpeg') {
    context.fillStyle = JPEG_BACKGROUND
    context.fillRect(0, 0, width, height)
  }
  context.drawImage(image, 0, 0)
}
